import React from 'react';

import { useIntl } from 'ha/i18n';

import { makeStyles } from 'tss-react/mui';
import { PaperBadge as Badge, Grid, units } from '@ha/dsl';
import { track } from '@ha/analytics';

import StudapartIcon from 'ha/custom-icons/studapart.svg';
import { FavoriteButtonNext as FavoriteButton } from 'ha/components/FavoriteButton';

import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';
import { useFavoriteListing } from 'ha/modules/FavoriteListing';
import { useMulFeatureFlags } from 'ha/modules/MultiUnitListings/hooks';

import { useConfig } from 'ha/helpers/config';

const useStyles = makeStyles()(theme => ({
  absolute: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  badge: {
    background: theme.palette.white.main,
    color: theme.palette.orange.dark,
    borderRadius: units.rem(0.875),
    fontSize: units.rem(0.875),
    padding: units.yx(0.18, 0.55),
  },
  cancellationBadge: {
    color: theme.palette.grey['700'],
  },
  content: {
    padding: theme.spacing(1.5),
  },
  favoriteButton: {
    marginLeft: 'auto',
  },
  interactable: {
    zIndex: 2,
  },
  studapartBadge: {
    display: 'flex',
    position: 'relative',
    background: 'rgba(255,255,255,0.9)',
    borderRadius: 'initial',
    borderTopRightRadius: units.rem(0.875),
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
  },
  studapartIcon: {
    width: units.rem(6),
  },
}));

interface Props {
  room: NormalizedAlgoliaListing;
  isCancellationFlexible: boolean;
  showMoreMediaSlide: boolean;
  withFavorites?: boolean;
}

const PhotoGalleryOverlayActions: React.FC<Props> = ({
  room: {
    id,
    isNew,
    advertiserId,
    isPartner,
    unitTypeId,
    isMultiUnit,
    country,
  },
  isCancellationFlexible,
  showMoreMediaSlide,
  withFavorites,
}) => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();

  const { studapart } = useConfig();

  const { isTenantMULFlowEnabled } = useMulFeatureFlags(country);

  // TODO when we enabled isTenantMULFlowEnabled for all the Countries then
  // switch to use the unit type end points
  const {
    onClick: onFavoriteClick,
    isLoading: isFavoriteLoading,
    isFavorite,
  } = useFavoriteListing(
    id, // isTenantMULFlowEnabled ? unitTypeId : id,
    false, // isTenantMULFlowEnabled,
  );

  const handleFavoritesClick = React.useCallback(
    (e: React.MouseEvent) => {
      onFavoriteClick(e);

      track('Favorite clicked', {
        type: 'button',
        ...(isTenantMULFlowEnabled && {
          unitTypeId,
          isMultiUnit,
          unitId: id,
        }),
        listingId: id,
        favoriteType: isFavorite ? 'unfavorite' : 'favorite',
      });
    },
    [
      onFavoriteClick,
      id,
      isFavorite,
      unitTypeId,
      isMultiUnit,
      isTenantMULFlowEnabled,
    ],
  );

  return (
    <Grid
      className={cx(classes.absolute, classes.content)}
      container
      direction="column"
      justifyContent="space-between"
    >
      <Grid item container justifyContent="space-between">
        {isCancellationFlexible && !showMoreMediaSlide && (
          <Grid item className={classes.interactable}>
            <Badge className={cx(classes.badge, classes.cancellationBadge)}>
              {T('search.cancellation_policy.tag.flexible_cancellation')}
            </Badge>
          </Grid>
        )}
        {isNew && !isCancellationFlexible && !showMoreMediaSlide && (
          <Grid item className={classes.interactable}>
            <Badge className={classes.badge}>{T('New')}</Badge>
          </Grid>
        )}
        {withFavorites && (
          <Grid
            item
            className={cx(classes.favoriteButton, classes.interactable)}
          >
            <FavoriteButton
              size="xs"
              onClick={handleFavoritesClick}
              isLoading={isFavoriteLoading}
              isFavorite={isFavorite}
            />
          </Grid>
        )}
      </Grid>
      {isPartner && advertiserId === studapart.advertiserId && (
        <Grid item container justifyContent="space-between">
          <Grid item className={classes.interactable}>
            <Badge className={cx(classes.badge, classes.studapartBadge)}>
              <StudapartIcon className={classes.studapartIcon} />
            </Badge>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export { PhotoGalleryOverlayActions };
